import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import { useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

import {
  EducationLevel,
  GetUserEducationQuery,
  UpsertUserEducationRequest,
} from '@/_gql/graphql'

import { gqlFetch } from '@/services/graphql/fetcher'
import { EDUCATION_LEVEL_OPTIONS } from '@/services/user/constant'
import { GqlUserEducationUpsert } from '@/services/user/gql'
import { queryUserEducation } from '@/services/user/query'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormField } from '@/components/ui/form'
import { FormFieldset } from '@/components/ui/form-field/fieldset'
import { FormFieldSelect } from '@/components/ui/form-field/select'
import { Input } from '@/components/ui/input'
import { InputMonthPicker } from '@/components/ui/input-month-picker'

import {
  formatDateOnly,
  formatDateTimeServer,
  normalizeEnum,
} from '@/lib/utils/utils'

import { CardProfile } from '../digishoku/profile/-components/card-profile'

export const Route = createFileRoute('/_user/profile/education')({
  loader: async ({ context: { queryClient: qc } }) => {
    const [education] = await Promise.all([qc.fetchQuery(queryUserEducation())])

    return {
      education,
      async refetch() {
        await qc.refetchQueries(queryUserEducation())
      },
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { education, refetch } = Route.useLoaderData()
  const navigate = Route.useNavigate()
  const form = useForm<FormDto>({
    resolver: zodResolver(FormDto),
    defaultValues: getDefaultValues(education),
  })

  const educationFields = useFieldArray({
    control: form.control,
    name: 'education',
    keyName: 'eId',
  })

  const { mutateAsync: mutateEducationAsync } = useMutation({
    mutationFn: async (data: FormDto) => {
      await gqlFetch({
        query: GqlUserEducationUpsert,
        variables: { request: { request: formDtoToPayloadEducation(data) } },
      })
      await refetch()
    },
  })

  const handleOnSubmit = form.handleSubmit((data) => {
    const promises = []

    if (data.education.length > 0) {
      promises.push(mutateEducationAsync(data))
    }

    if (promises.length === 0) {
      toast.error('No data to submit')
      return
    }

    navigate({ to: '/profile/experience' })

    toast.promise(Promise.all(promises), {
      loading: 'Menyimpan informasi penddikan',
      success: 'Informasi penddikan berhasil disimpan',
      error: 'Terjadi kesalahan saat menyimpan informasi',
    })
  }, console.error)
  const showRemoveEduction = educationFields.fields.length > 1

  return (
    <Form {...form}>
      <form onSubmit={handleOnSubmit}>
        <CardProfile title="Riwayat Pendidikan" isRequired={true}>
          <CardProfile.Content className="flex flex-col">
            {educationFields.fields.map((field, idx) => (
              <EducationFormItem
                key={field.eId}
                idx={idx}
                showRemove={showRemoveEduction}
                onRemove={() => {
                  educationFields.remove(idx)
                }}
              />
            ))}
            <div className="col-span-3">
              <Button
                className="w-full rounded-xl border-primary-yes text-primary-yes md:w-auto"
                variant="outline"
                type="button"
                onClick={() => educationFields.append(defaultItemEducation())}
              >
                <PlusIcon />
                Tambah Riwayat Pendidikan
              </Button>
            </div>
          </CardProfile.Content>
          <CardProfile.Footer>
            <CardProfile.SubmissionButton />
          </CardProfile.Footer>
        </CardProfile>
      </form>
    </Form>
  )
}

interface EducationFormItemProps {
  idx: number
  showRemove: boolean
  onRemove?: () => void
}

function EducationFormItem(props: EducationFormItemProps) {
  const { idx, showRemove, onRemove } = props
  const form = useFormContext<FormDto>()

  return (
    <div className="relative col-span-full grid grid-cols-subgrid gap-x-2 gap-y-6 rounded-md border border-border p-4 py-6">
      <div className="absolute right-0 top-0">
        <Button variant="ghost" size="icon" onClick={onRemove} type="button">
          <Trash2Icon className="size-16 text-primary-yes" />
        </Button>
      </div>

      <FormField
        control={form.control}
        name={`education.${idx}.school`}
        render={({ field }) => (
          <FormFieldset
            label="Nama sekolah/kampus"
            className="col-span-full md:col-span-3"
          >
            <Input placeholder="Masukkan nama sekolah/kampus" {...field} />
          </FormFieldset>
        )}
      />

      <FormField
        control={form.control}
        name={`education.${idx}.level`}
        render={({ field }) => (
          <FormFieldset
            label="Tingkatan pendidikan"
            className="col-span-full md:col-span-3"
          >
            <FormFieldSelect
              placeholder="Pilih tingkatan pendidikan"
              items={EDUCATION_LEVEL_OPTIONS}
              value={field.value}
              onValueChange={field.onChange}
            />
          </FormFieldset>
        )}
      />

      {/* Grid untuk field yang berdampingan */}
      <div className="col-span-full grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Negara */}
        <FormField
          control={form.control}
          name={`education.${idx}.country`}
          render={({ field }) => (
            <FormFieldset label="Negara" className="col-span-full">
              <Input placeholder="Masukkan negara" {...field} />
            </FormFieldset>
          )}
        />

        {/* Nilai Kumulatif */}
        <FormField
          control={form.control}
          name={`education.${idx}.cumulative_grade`}
          render={({ field }) => (
            <FormFieldset label="Nilai Kumulatif" className="col-span-full">
              <Input
                type="number"
                step="0.01"
                placeholder="Contoh: 3.75"
                {...field}
                onChange={(e) =>
                  field.onChange(parseFloat(e.target.value) || '')
                }
              />
            </FormFieldset>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name={`education.${idx}.major`}
        render={({ field }) => (
          <FormFieldset label="Bidang Studi" className="col-span-full">
            <Input placeholder="Masukkan Bidang Studi" {...field} />
          </FormFieldset>
        )}
      />

      <div className="col-span-full space-y-2 md:col-span-3">
        <FormField
          control={form.control}
          name={`education.${idx}.start_date`}
          render={({ field }) => (
            <FormFieldset label="Tanggal mulai" noFormControl>
              <InputMonthPicker
                asForm
                value={field.value || undefined}
                onChange={field.onChange}
                placeholder="Pilih Tanggal"
              />
            </FormFieldset>
          )}
        />
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`education-${idx}-graduation-date-checkbox`}
            checked={form.watch(`education.${idx}.graduation_date`) === null}
            onCheckedChange={(checked) => {
              form.setValue(
                `education.${idx}.graduation_date`,
                checked ? null : undefined!
              )
            }}
          />
          <label
            id={`education-${idx}-graduation-date-checkbox`}
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Saya masih sekolah/kuliah di tempat ini
          </label>
        </div>
      </div>
      <FormField
        control={form.control}
        name={`education.${idx}.graduation_date`}
        render={({ field }) => (
          <FormFieldset
            label="Tanggal lulus"
            noFormControl
            className="col-span-full md:col-span-3"
          >
            <InputMonthPicker
              asForm
              value={field.value || undefined}
              onChange={field.onChange}
              placeholder="Pilih Tanggal"
            />
          </FormFieldset>
        )}
      />
    </div>
  )
}

interface FormDto extends z.infer<typeof FormDto> {}
const FormDto = z.object({
  education: z.array(
    z.object({
      id: z.coerce.number().optional(),
      school: z.string().min(1, 'Nama sekolah tidak boleh kosong'),
      level: z.preprocess(
        (val) => {
          if (typeof val === 'string' && val.trim() === '') return undefined
          return val
        },
        z.nativeEnum(EducationLevel, {
          required_error: 'Tingkatan pendidikan tidak boleh kosong',
          invalid_type_error: 'Tingkatan pendidikan tidak boleh kosong',
        })
      ),
      country: z.string().min(1, 'Negara tidak boleh kosong'),
      cumulative_grade: z.coerce
        .number()
        .min(0, 'Nilai tidak boleh kurang dari 0')
        .max(4, 'Nilai tidak boleh lebih dari 4')
        .optional(),
      major: z.string().min(1, 'Bidang studi tidak boleh kosong'),
      start_date: z.preprocess(
        (val) => {
          if (!val) return undefined // handle null, undefined, empty string
          const d = new Date(val as any)
          return isNaN(d.getTime()) ? undefined : d // if invalid date -> undefined
        },
        z.date({
          required_error: 'Pilih tanggal mulai dengan benar',
          invalid_type_error: 'Pilih tanggal mulai dengan benar',
        })
      ),
      graduation_date: z.coerce.date().nullish(),
      created_at: z.coerce.date().optional(),
    })
  ),
})

type EducationData = GetUserEducationQuery['getUserEducation']['data'] | null

function getDefaultValues(initialEducation?: EducationData): FormDto {
  const data: FormDto = {
    education: (initialEducation ?? [])?.map((x) => ({
      ...x,
      level: normalizeEnum(x.level)!,
      // Handle new fields with proper fallbacks
      country: (x as any)?.country || '',
      cumulative_grade: (x as any)?.cumulative_grade || undefined,
      start_date: x.start_date ? new Date(x.start_date) : undefined!,
      graduation_date: x.graduation_date ? new Date(x.graduation_date) : null,
      created_at: x.created_at ? new Date(x.created_at) : undefined!,
    })),
  }

  if (!data.education.length) data.education.push(defaultItemEducation())

  return data
}

function defaultItemEducation(): FormDto['education'][number] {
  return {
    id: undefined!,
    school: '',
    level: undefined!,
    country: '',
    cumulative_grade: undefined,
    major: '',
    start_date: undefined!,
    graduation_date: undefined!,
    created_at: undefined!,
  }
}

function formDtoToPayloadEducation(
  data: FormDto
): UpsertUserEducationRequest['request'] {
  return data.education.map((x) => ({
    id: x.id ?? undefined,
    school: x.school,
    level: x.level,
    country: x.country,
    cumulative_grade: x.cumulative_grade?.toString(),
    major: x.major,
    start_date: formatDateOnly(x.start_date),
    graduation_date: x.graduation_date
      ? formatDateOnly(x.graduation_date)
      : undefined,
    created_at: x.created_at ? formatDateTimeServer(x.created_at) : undefined,
  }))
}
